import React, { useEffect, useState } from 'react';
import PageContainer from '../../../components/PageContainer';
import { Box, Button, ButtonGroup, Card, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Grid, IconButton } from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { AccountBalance, Add, ArrowBack, DeleteOutline, Groups, GroupsOutlined, People, Search } from '@mui/icons-material';
import TextInput from '../../../components/common/TextInput';
import SelectInput from '../../../components/common/SelectInput';
import { Link, useHistory, useParams } from 'react-router-dom';
import appRoutes from '../../../routes/routeConfig';
import { assignUserRole, createUser, editUser, getRoles, getUserById, getUserRoles, removeUserRole } from '../../../redux/api/userMgt';
import { useSnackbar } from 'notistack';
import { DataGrid } from '@mui/x-data-grid';
import { addOrUpdateInstitution, getInstitutionById, getInstitutionTypes } from '../../../redux/api/institution';

const InstitutionCreate = () => {
    const history = useHistory();
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();
    const formFields = {
        isActive: true,
        institutionTypeId: '',
        name: '',
        code: '',
        email: '',
        institutionTypeName: '',
    };
    const [institution, setInstitution] = useState(formFields);
    const [processing, setProcessing] = useState(false);

    const [isEditing, setIsEditing] = useState(false);
    const [actionModal, setActionModal] = useState(null);

    const getInfo = async (instId) => {
        try {
            setProcessing(true);
            const response = await getInstitutionById(instId);
            setInstitution({
                ...response,
            });
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', { variant: 'error' });
        } finally {
            setProcessing(false);
        }
    };

    const submitForm = async (values, { resetForm }) => {
        try {
            const { isSuccess, message } = await addOrUpdateInstitution(values);
            if (isSuccess) {
                enqueueSnackbar(isEditing ? 'Institution Updated Successfully' : 'Institution Created Successfully');
                history.push(appRoutes.internal.institutions.root);
                resetForm();
            } else {
                enqueueSnackbar(message ?? 'An error occurred', { variant: 'error' });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', { variant: 'error' });
        }
    };

    useEffect(() => {
        if (params?.instId) {
            setIsEditing(true);
            getInfo(params?.instId);
        } else {
            setInstitution(formFields);
            setIsEditing(false);
        }
    }, [params]);

    return (
        <PageContainer
            pageTitle={isEditing ? 'Edit Institution' : 'Create Institution'}
            tagline={isEditing ? 'Manage institution' : 'Create new institution'}
            processing={processing}
            buttonComponent={
                <>
                    <Link to={appRoutes.internal.institutions.root}>
                        <Button startIcon={<ArrowBack />} variant="outlined">
                            Go Back
                        </Button>
                    </Link>
                </>
            }
        >
            <Card className="paper">
                <Formik
                    initialValues={{ ...institution }}
                    enableReinitialize
                    onSubmit={submitForm}
                    validationSchema={Yup.object().shape({
                        name: Yup.string().required('Institution Name is required.'),
                        code: Yup.string().required('Institution Code is required.'),
                        email: Yup.string().email('Value must be a valid email address').required('Institution Email is required.'),
                        institutionTypeId: Yup.string().required('Institution Type is required.'),
                    })}
                >
                    {({ isSubmitting, values }) => (
                        <Form>
                            <Grid container spacing={3} paddingBottom={3}>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <TextInput id="code" required label="Institution Code" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <TextInput id="name" required label="Institution Name" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <TextInput id="email" required label="Institution Email" type="email" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <SelectInput required id="institutionTypeId" label="Institution Type" selectFunction={getInstitutionTypes} field="institutionTypeId" fieldDisplay="name" />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <Button disabled={isSubmitting} startIcon={!isEditing && <Add />} fullWidth variant="contained" type="submit" style={{ marginTop: '.5rem' }}>
                                        {isSubmitting ? <CircularProgress size={15} color="inherit" /> : isEditing ? 'Edit Institution' : 'Add Institution'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Card>
        </PageContainer>
    );
};

export default InstitutionCreate;
